import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useContext, useEffect, useRef, useState } from "react";
import { colord } from "colord";
import { useStringMap } from "../../utilities/string-map/useStringMap";
import { Input } from "../input/Input";
import { CancelXIcon } from "../../icons/CancelXIcon";
import { ColorIcon } from "../../icons/ColorIcon";
import { UndoIcon } from "../../icons/UndoIcon";
import { ColorPicker } from "../color-picker/ColorPicker";
import { useCombineRefs } from "../../hooks/useCombineRefs";
import { FieldsetContext } from "../fieldset/Fieldset";
import { IconButton } from "../../icon-button/IconButton";
import * as Styled from "./ColorField.styled";
import { RGBAInputs } from "./RGBAInputs";
import { stringMapDefault } from "./ColorField.stringMap";
const componentName = "ColorField";
export const ColorField = forwardRef((props, ref) => {
    const { colorPickerProps, defaultColor = "", disabled: disabledProp, gutterAdornment, hasVariantLabel = true, inputProps, onColorChange, placeholder, shouldDisableColorPicker, stringMap: stringMapProp, value, variant = "default", ...otherProps } = props;
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    const fieldsetOptions = useContext(FieldsetContext);
    const disabled = disabledProp || fieldsetOptions.disabled;
    const inputRef = useRef(null);
    const combinedRef = useCombineRefs(ref, inputRef);
    const [activeColor, setActiveColor] = useState(defaultColor);
    const [activeOpacity, setActiveOpacity] = useState("1");
    const [hasError, setHasError] = useState(false);
    useEffect(() => {
        if (!value || value === "") {
            return;
        }
        if (!colord(value).isValid()) {
            setHasError(true);
        }
        if (colord(value).isValid()) {
            setHasError(false);
        }
    }, [value]);
    useEffect(() => {
        if (variant !== "rgba") {
            const input = inputRef.current;
            if (value !== undefined && value !== input.value) {
                setActiveColor(value);
                input.value = value;
            }
        }
    }, [value, variant]);
    // Color Swatch
    const getHexStartAdornment = () => {
        const defaultIcon = _jsx(ColorIcon, { "aria-label": stringMap.ariaLabelTriggerIcon });
        if (shouldDisableColorPicker) {
            return activeColor ? _jsx(Styled.ColorFieldColorSwatch, { backgroundColor: activeColor }) : defaultIcon;
        }
        return (_jsx(ColorPicker, { ...colorPickerProps, hasConfirmButtons: false, selectedColor: activeColor, hasColorField: false, hasColorPaletteSelect: false, onChange: ({ color, opacity, isValidCssColor }) => {
                if (color !== activeColor) {
                    setActiveColor(color);
                    setActiveOpacity(opacity);
                    setHasError(!isValidCssColor);
                    onColorChange && onColorChange({ color, opacity, isValidCssColor });
                }
            }, trigger: _jsx(IconButton, { icon: activeColor ? (_jsx(Styled.ColorFieldColorSwatch, { backgroundColor: activeColor, children: _jsx(Styled.ColorFieldVisuallyHiddenText, { children: stringMap.ariaLabelTriggerIcon }) })) : (defaultIcon), tooltipContents: stringMap.colorSwatchTooltip }) }));
    };
    // Clear/Undo
    const getHexEndAdornment = () => {
        const clearIcon = defaultColor === "" ? (_jsx(CancelXIcon, { "aria-label": stringMap.ariaLabelClearIcon })) : (_jsx(UndoIcon, { "aria-label": stringMap.ariaLabelUndoIcon }));
        const tooltipContents = defaultColor ? `${stringMap.resetTooltip} (${defaultColor})` : stringMap.clearTooltip;
        if (activeColor !== undefined && activeColor.toLowerCase() !== defaultColor.toLowerCase()) {
            return (_jsx(IconButton, { icon: clearIcon, tooltipContents: tooltipContents, onClick: (e) => {
                    e.stopPropagation(); // prevent shouldCloseOnOutsideInteraction=true from being called when in ColorPicker
                    const nextColor = defaultColor || "";
                    setActiveColor(nextColor);
                    setActiveOpacity("1");
                    setHasError(false);
                    onColorChange && onColorChange({ isValidCssColor: true, color: nextColor, opacity: "1" });
                } }));
        }
        return null;
    };
    if (variant === "rgba") {
        return (_jsx(RGBAInputs, { activeColor: activeColor, setActiveColor: setActiveColor, onColorChange: onColorChange, value: value, gutterAdornment: gutterAdornment, disabled: disabled, hasVariantLabel: hasVariantLabel }));
    }
    return (_jsx(Styled.ColorField, { ref: combinedRef, "data-testid": "ColorField", ...otherProps, disabled: disabled, input: _jsx(Input, { value: activeColor || "", placeholder: placeholder, onChange: (e) => setActiveColor(e.target.value), onBlur: (event) => {
                const { value } = event.target;
                const isValid = colord(value).isValid();
                setHasError(!isValid);
                onColorChange && onColorChange({ color: value, isValidCssColor: isValid, opacity: activeOpacity });
            }, ...inputProps }), status: hasError ? { level: "error", message: stringMap.errorMessage } : undefined, startAdornment: getHexStartAdornment(), endAdornment: disabled ? undefined : getHexEndAdornment(), gutterAdornment: gutterAdornment, description: hasVariantLabel ? _jsx(Styled.ColorFieldVariantText, { children: "hex" }) : undefined }));
});
ColorField.displayName = componentName;
